import { Device } from '../helpers'
import { domReady } from '../helpers/DOM'
import Logger from '../helpers/Logger'
import type WebModel from '../models/WebModel'
import { OUTSTREAM_REFRESH_TIME } from '../outstream/OutstreamRenderer'
import type { Slot } from '../slots/Slot'
import type SlotFactory from '../slots/SlotFactory'
import { VIDEO_TARGET_CLASS } from '../stickyVideo/StickyVideo'

/**
 * StickyOutstreams class
 * Responsible for displaying StickyOutstream slots
 */
export default class StickyOutstreams {
  createdSlot: Slot | undefined
  renderedPromise: Promise<void>

  constructor(readonly webModel: WebModel, readonly slotFactory: SlotFactory) {
    // Skip if adhesions are not turned on for this device
    if (!this.shouldDisplayStickyOutstream()) {
      this.renderedPromise = Promise.resolve()
      return
    }
    this.renderedPromise = new Promise((resolve) => {
      domReady(() => {
        this.createAndDisplaySlot(slotFactory).then(() => {
          resolve()
        })
      })
    })
  }

  shouldDisplayStickyOutstream(): boolean {
    const hasMvVideo = !!document.querySelector(`.${VIDEO_TARGET_CLASS}`)
    const isDesktop = !Device.isMobileOrTablet
    const isEnabled = this.webModel.desktop_outstream_adhesion

    // Don't want to show these ads on the homepage of a site. Matches StickyVideo behavior.
    const isRoot = window.location.pathname === '/'

    return isDesktop && isEnabled && !isRoot && !hasMvVideo
  }

  setupStickyOutstreamSlot(): ISlotModel {
    const slotModel: ISlotModel = {
      adUnitId: 'stickyOutstream_atf',
      type: 'StickyOutstream' as SlotClass,
      selector: 'body',
      insertPosition: 'beforeend',
      refreshTime: OUTSTREAM_REFRESH_TIME
    }
    return slotModel
  }

  // This function actually sets up the StickyOutstream slot on the page.
  // Called in Atf.ts.
  async createAndDisplaySlot(
    slotFactory: SlotFactory
  ): Promise<Slot | undefined> {
    try {
      const slot = await slotFactory.new(this.setupStickyOutstreamSlot())
      this.createdSlot = slot
      return slot
    } catch (e) {
      Logger.debug(e)
    }
  }
}
